import { apiClient } from '../lib/apiClient';

/**
 * Incidents and the intervention log (Reef Guard #1824): a ±14-day window
 * around a livestock death, a critical finding or a manual "something is
 * wrong", with deterministic candidate causes; and "I changed X" entries.
 */
export type IncidentTrigger = 'livestock_death' | 'critical_finding' | 'manual';

export interface Incident {
  readonly id: string;
  readonly aquariumId: string;
  readonly triggerType: IncidentTrigger;
  readonly triggerRefId: string | null;
  readonly triggerLabel: string | null;
  readonly anchorAt: string;
  readonly note: string | null;
  readonly closed: boolean;
  readonly createdAt: string;
  /** Emergency mode (UC-18): the situation and the ticked first-aid steps. */
  readonly situation?: string | null;
  readonly checklistProgress?: ChecklistProgressItem[];
}

/** A ticked step of the emergency checklist and when it was ticked. */
export interface ChecklistProgressItem {
  readonly step: string;
  readonly at: string;
}

export type IncidentEventType =
  | 'measurement'
  | 'dosing'
  | 'livestock_added'
  | 'livestock_deceased'
  | 'task'
  | 'journal_entry'
  | 'image'
  | 'icp_analysis'
  | 'finding'
  | 'intervention';

export interface IncidentEvent {
  readonly id: string;
  readonly type: IncidentEventType;
  readonly at: string;
  readonly title: string | null;
  readonly values: Readonly<Record<string, string | number | null>>;
  readonly deepLinkPath: string | null;
}

export type CauseRule = 'swing' | 'out_of_range' | 'livestock_cluster' | 'dose_spike' | 'guard_finding' | 'recent_change' | 'no_tests';

export interface CandidateCause {
  readonly rule: CauseRule;
  readonly severity: 'high' | 'medium' | 'info';
  readonly parameterIds: readonly string[];
  readonly at: string;
  readonly daysBefore: number;
  readonly values: Readonly<Record<string, string | number>>;
}

export interface IncidentDetail {
  readonly incident: Incident;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly events: IncidentEvent[];
  readonly causes: CandidateCause[];
  readonly truncated: boolean;
}

export const INTERVENTION_KINDS = [
  'product_started',
  'product_stopped',
  'water_change',
  'blackout',
  'uv_on',
  'uv_off',
  'carbon',
  'nutrients_raised',
  'equipment_failure',
  'maintenance',
  'other',
] as const;
export type InterventionKind = (typeof INTERVENTION_KINDS)[number];

export interface Intervention {
  readonly id: string;
  readonly aquariumId: string;
  readonly kind: InterventionKind;
  readonly note: string | null;
  readonly occurredAt: string;
  readonly endedAt: string | null;
  readonly createdAt: string;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}`;

export const incidentsKey = (aquariumId: string) => ['incidents', aquariumId] as const;
export const incidentKey = (aquariumId: string, id: string) => ['incidents', aquariumId, id] as const;
export const interventionsKey = (aquariumId: string) => ['interventions', aquariumId] as const;

export async function listIncidents(aquariumId: string): Promise<Incident[]> {
  const res = await apiClient.get<{ data: Incident[] }>(`${base(aquariumId)}/incidents`);
  return Array.isArray(res?.data) ? res.data : [];
}

export async function getIncident(aquariumId: string, id: string): Promise<IncidentDetail> {
  const res = await apiClient.get<{ data: IncidentDetail }>(`${base(aquariumId)}/incidents/${encodeURIComponent(id)}`);
  return res.data;
}

export async function createIncident(
  aquariumId: string,
  input: {
    anchor_at: string;
    note?: string | null;
    /** Emergency mode: the situation, the ticked steps and a per-visit token (a double tap creates one incident). */
    situation?: string;
    checklist_progress?: ChecklistProgressItem[];
    client_token?: string;
  },
): Promise<Incident> {
  const res = await apiClient.post<{ data: Incident }>(`${base(aquariumId)}/incidents`, input);
  return res.data;
}

export async function updateIncident(
  aquariumId: string,
  id: string,
  patch: { note?: string | null; closed?: boolean; situation?: string; checklist_progress?: ChecklistProgressItem[] },
): Promise<Incident> {
  const res = await apiClient.patch<{ data: Incident }>(`${base(aquariumId)}/incidents/${encodeURIComponent(id)}`, patch);
  return res.data;
}

export async function deleteIncident(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/incidents/${encodeURIComponent(id)}`);
}

export async function listInterventions(aquariumId: string, range: { from?: string; to?: string } = {}): Promise<Intervention[]> {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const query = params.toString();
  const res = await apiClient.get<{ data: Intervention[] }>(`${base(aquariumId)}/interventions${query ? `?${query}` : ''}`);
  return Array.isArray(res?.data) ? res.data : [];
}

export async function createIntervention(
  aquariumId: string,
  input: { kind: InterventionKind; note?: string | null; occurred_at: string; ended_at?: string | null },
): Promise<Intervention> {
  const res = await apiClient.post<{ data: Intervention }>(`${base(aquariumId)}/interventions`, input);
  return res.data;
}

export async function deleteIntervention(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete(`${base(aquariumId)}/interventions/${encodeURIComponent(id)}`);
}
